import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { Switch } from '@/components/ui/Switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/Dialog';
import { ErrorState, LoadingState } from '@/components/ui/States';
import { toast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api/client';
import { keys } from '@/lib/queryKeys';
import { cn } from '@/lib/cn';
import * as qpApi from './api';
import type {
  Assignment, AutoRule, CustomCategoryRule, CustomKey, QueuePriorityAdminView, QueueRules, RoutineCategory, RuleGender,
  TravelBasis,
} from './types';

/**
 * Admin Panel → Facility → Queue Prioritization (admin:queue-priority).
 *
 * Per BRANCH — the queue is branch-scoped, so each site has its own rules;
 * the top-bar branch switcher picks which one this screen edits. The backend
 * (utils/queuePriority.js) is what actually orders the queue — this screen
 * only edits the settings it reads, and the server validates every value
 * again. Emergency and clinically urgent patients are fixed at the top and
 * are shown here locked, never editable.
 *
 * Besides the four built-in categories, an admin can add custom ones
 * ("+ Add custom priority") — a name, a rank, a waiting-time limit and how
 * patients join it: Manual (staff tick it at the Queue stage) or Automatic
 * (the system decides from the patient's age / gender). Long-distance travel
 * can also be Manual (staff enter the km) or Automatic (patient address vs
 * this branch's); the other built-ins' assignment is fixed and shown read-only.
 */

const ROUTINE: RoutineCategory[] = ['appointment', 'referral', 'travel', 'walk_in'];
type RowId = RoutineCategory | CustomKey;

const LABEL: Record<RoutineCategory, string> = {
  appointment: 'Appointment-booked patients',
  referral: 'Referred patients (routine)',
  travel: 'Long-distance travellers',
  walk_in: 'Normal walk-in patients',
};

type Draft = { rules: QueueRules; safeguard_enabled: boolean };
type Errors = Record<string, string>;
interface CommonRule { enabled: boolean; rank: number; max_wait_minutes: number | null }

const isCustom = (id: RowId): id is CustomKey => id.startsWith('c_');

function clone(d: Draft): Draft {
  return { rules: JSON.parse(JSON.stringify(d.rules)) as QueueRules, safeguard_enabled: d.safeguard_enabled };
}

function sameDraft(a: Draft, b: Draft): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function customOf(d: Draft, id: CustomKey): CustomCategoryRule | undefined {
  return d.rules.custom.find((c) => c.key === id);
}

function ruleOf(d: Draft, id: RowId): CommonRule {
  return isCustom(id) ? customOf(d, id)! : d.rules[id];
}

function labelOf(d: Draft, id: RowId): string {
  return isCustom(id) ? customOf(d, id)?.label ?? id : LABEL[id];
}

/** Server error keys address custom categories by list position. */
function errKey(d: Draft, id: RowId, field: string): string {
  return isCustom(id) ? `rules.custom.${d.rules.custom.findIndex((c) => c.key === id)}.${field}` : `rules.${id}.${field}`;
}

function allIds(d: Draft): RowId[] {
  return [...ROUTINE, ...d.rules.custom.map((c) => c.key)];
}

function newKey(label: string, d: Draft): CustomKey {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 28) || 'category';
  let key: CustomKey = `c_${slug}`;
  for (let n = 2; d.rules.custom.some((c) => c.key === key); n += 1) key = `c_${slug}_${n}`;
  return key;
}

const GENDER_LABEL: Record<RuleGender, string> = { male: 'male', female: 'female', other: 'other gender' };

/** "female patients aged 18–45", "patients aged 60 or over", … */
function ruleText(r: AutoRule | null): string {
  if (!r) return 'no rule set';
  const who = r.gender ? `${GENDER_LABEL[r.gender]} patients` : 'patients';
  if (r.min_age != null && r.max_age != null) return `${who} aged ${r.min_age}–${r.max_age}`;
  if (r.min_age != null) return `${who} aged ${r.min_age} or over`;
  if (r.max_age != null) return `${who} aged ${r.max_age} or under`;
  return who;
}

/** Mirrors the server's checkAutoRule. Returns an error message or undefined. */
function autoRuleError(r: AutoRule | null, limits: QueuePriorityAdminView['limits']): string | undefined {
  if (!r || (r.min_age == null && r.max_age == null && r.gender == null)) return 'Set at least one condition (age or gender)';
  const [aMin, aMax] = limits.age;
  const bad = (a: number | null) => a != null && (!Number.isInteger(a) || a < aMin || a > aMax);
  if (bad(r.min_age) || bad(r.max_age)) return `Ages must be ${aMin}–${aMax}`;
  if (r.min_age != null && r.max_age != null && r.min_age > r.max_age) return 'The lower age must not be above the upper age';
  return undefined;
}

/** Automatic long-distance travel, in words. */
function travelAutoText(t: QueueRules['travel']): string {
  if (t.auto_basis === 'state') return 'Patient’s address is in a different state from this branch';
  if (t.auto_basis === 'pincode') {
    return t.local_pincode_prefixes.length
      ? `Patient’s PIN code does not start with ${t.local_pincode_prefixes.join(', ')}`
      : 'Patient’s PIN code is not local (no local PIN codes set yet)';
  }
  return 'Patient’s address is in a different district from this branch';
}

/** Client-side mirror of the server's checks, for immediate feedback. The server remains the authority. */
function validate(d: Draft, limits: QueuePriorityAdminView['limits']): Errors {
  const e: Errors = {};
  const [rMin, rMax] = limits.rank;
  const [wMin, wMax] = limits.max_wait_minutes;
  const [kMin, kMax] = limits.min_distance_km;
  const [lMin, lMax] = limits.label_length;
  for (const id of allIds(d)) {
    const r = ruleOf(d, id);
    if (!Number.isInteger(r.rank) || r.rank < rMin || r.rank > rMax) e[errKey(d, id, 'rank')] = `Rank must be ${rMin}–${rMax}`;
    if (r.max_wait_minutes !== null && (!Number.isInteger(r.max_wait_minutes) || r.max_wait_minutes < wMin || r.max_wait_minutes > wMax)) {
      e[errKey(d, id, 'max_wait_minutes')] = `Leave empty or enter ${wMin}–${wMax} minutes`;
    }
  }
  const km = d.rules.travel.min_distance_km;
  if (!Number.isInteger(km) || km < kMin || km > kMax) e['rules.travel.min_distance_km'] = `Enter ${kMin}–${kMax} km`;
  const t = d.rules.travel;
  if (t.assignment === 'automatic' && t.auto_basis === 'pincode') {
    if (!t.local_pincode_prefixes.length) e['rules.travel.local_pincode_prefixes'] = 'Enter at least one local PIN code beginning (e.g. 600)';
    else if (t.local_pincode_prefixes.some((p) => !/^[1-9][0-9]{0,5}$/.test(p))) e['rules.travel.local_pincode_prefixes'] = 'Each must be 1–6 digits and not start with 0';
  }
  const seen = new Set<string>();
  d.rules.custom.forEach((c, i) => {
    const name = c.label.trim();
    if (name.length < lMin || name.length > lMax) e[`rules.custom.${i}.label`] = `Name must be ${lMin}–${lMax} characters`;
    else if (seen.has(name.toLowerCase())) e[`rules.custom.${i}.label`] = 'Another custom priority already has this name';
    seen.add(name.toLowerCase());
    if ((c.description ?? '').length > limits.description_length) e[`rules.custom.${i}.description`] = `At most ${limits.description_length} characters`;
    if (c.assignment === 'automatic') {
      const ruleErr = autoRuleError(c.auto_rule, limits);
      if (ruleErr) e[`rules.custom.${i}.auto_rule`] = ruleErr;
    }
  });
  return e;
}

function conditionText(id: RowId, d: Draft, grace: number): string {
  if (isCustom(id)) {
    const c = customOf(d, id)!;
    const how = c.assignment === 'automatic'
      ? `Applied automatically to ${ruleText(c.auto_rule)} (age from date of birth, else the recorded age).`
      : `Staff tick “${c.label}” at the Queue stage.`;
    return `${how}${c.description ? ` ${c.description}` : ''}`;
  }
  const r = d.rules;
  switch (id) {
    case 'appointment':
      if (!r.appointment.enabled) return 'Off — booked patients are ordered with walk-ins by check-in time.';
      return r.appointment.policy === 'slot_time'
        ? `Booked for today and checked in. Ordered by slot time if they arrive no more than ${grace} min late, otherwise by check-in time.`
        : 'Booked for today and checked in. Ordered by check-in time.';
    case 'referral':
      return r.referral.require_referral_source
        ? 'Marked as a routine referral at the Queue stage, with the referring doctor or facility recorded.'
        : 'Marked as a routine referral at the Queue stage.';
    case 'travel':
      return r.travel.assignment === 'automatic'
        ? `${travelAutoText(r.travel)} (from the patient record).`
        : `Travel distance of ${r.travel.min_distance_km} km or more, recorded at the Queue stage.`;
    case 'walk_in':
      return 'Everyone else. Ordered by check-in time. Cannot be turned off.';
  }
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export default function QueuePriorityTab() {
  const { branchVersion } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: [...keys.queue.prioritySettings(), branchVersion],
    queryFn: qpApi.getQueuePriority,
  });
  const history = useQuery({
    queryKey: [...keys.queue.priorityHistory(), branchVersion],
    queryFn: qpApi.getQueuePriorityHistory,
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<RowId | null>(null);
  const [reason, setReason] = useState('');
  const [serverErrors, setServerErrors] = useState<Errors>({});
  const [stale, setStale] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [adding, setAdding] = useState(false);

  const loaded: Draft | null = q.data ? { rules: q.data.settings.rules, safeguard_enabled: q.data.settings.safeguard_enabled } : null;
  useEffect(() => {
    if (q.data) {
      setDraft(clone({ rules: q.data.settings.rules, safeguard_enabled: q.data.settings.safeguard_enabled }));
      setServerErrors({});
      setStale(false);
    }
  }, [q.data]);

  const afterWrite = (data: QueuePriorityAdminView, title: string) => {
    qc.setQueryData([...keys.queue.prioritySettings(), branchVersion], data);
    qc.invalidateQueries({ queryKey: keys.queue.priorityHistory() });
    qc.invalidateQueries({ queryKey: keys.queue.priorityRules() });
    qc.invalidateQueries({ queryKey: keys.queue.today() });
    setReason('');
    setEditing(null);
    toast({
      title: data.changed === false ? 'Nothing to save — no setting changed' : title,
      description: data.changed === false ? undefined
        : `Applies now to ${data.waiting_now} patient${data.waiting_now === 1 ? '' : 's'} waiting at this branch. Tokens and consultations in progress are not affected.`,
      variant: data.changed === false ? 'neutral' : 'success',
    });
  };
  const onWriteError = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'conflict-stale') {
      setStale(true);
      toast({ title: 'Not saved — changed by someone else', description: 'Reload to see their changes, then reapply yours.', variant: 'danger' });
      return;
    }
    if (e instanceof ApiError && e.code === 'validation' && e.details) {
      setServerErrors(Object.fromEntries(Object.entries(e.details).filter(([, v]) => typeof v === 'string')) as Errors);
    }
    toast({ title: 'Not saved', description: errorMessage(e), variant: 'danger' });
  };

  const save = useMutation({
    mutationFn: (d: Draft) => qpApi.saveQueuePriority({
      rules: d.rules, safeguard_enabled: d.safeguard_enabled,
      expected_version: q.data!.settings.version, reason: reason.trim(),
    }),
    onSuccess: (data) => afterWrite(data, 'Queue prioritization saved'),
    onError: onWriteError,
  });
  const reset = useMutation({
    mutationFn: () => qpApi.resetQueuePriority({ expected_version: q.data!.settings.version, reason: reason.trim() }),
    onSuccess: (data) => { setConfirmReset(false); afterWrite(data, 'Reset to the default rules'); },
    onError: (e) => { setConfirmReset(false); onWriteError(e); },
  });

  const clientErrors = useMemo(() => (draft && q.data ? validate(draft, q.data.limits) : {}), [draft, q.data]);
  const errors: Errors = { ...serverErrors, ...clientErrors };

  if (q.isLoading || (q.data && !draft)) return <LoadingState />;
  if (q.isError) return <ErrorState message={errorMessage(q.error)} />;
  const view = q.data!;
  const d = draft!;
  const dirty = !!loaded && !sameDraft(d, loaded);
  const reasonOk = reason.trim().length >= 3;
  const isDefault = sameDraft(d, view.defaults);
  const [rMin, rMax] = view.limits.rank;
  const canAdd = d.rules.custom.length < view.limits.max_custom;

  const update = (next: Draft) => { setServerErrors({}); setDraft(next); };
  const setBuiltin = <C extends RoutineCategory>(c: C, patch: Partial<QueueRules[C]>) =>
    update({ ...d, rules: { ...d.rules, [c]: { ...d.rules[c], ...patch } } });
  const setCustom = (key: CustomKey, patch: Partial<CustomCategoryRule>) =>
    update({ ...d, rules: { ...d.rules, custom: d.rules.custom.map((c) => (c.key === key ? { ...c, ...patch } : c)) } });
  const setCommon = (id: RowId, patch: Partial<CommonRule>) =>
    (isCustom(id) ? setCustom(id, patch) : setBuiltin(id, patch as Partial<QueueRules[typeof id]>));
  const removeCustom = (key: CustomKey) => {
    if (editing === key) setEditing(null);
    update({ ...d, rules: { ...d.rules, custom: d.rules.custom.filter((c) => c.key !== key) } });
  };
  const addCustom = (c: Omit<CustomCategoryRule, 'key'>) => {
    const key = newKey(c.label, d);
    update({ ...d, rules: { ...d.rules, custom: [...d.rules.custom, { ...c, key }] } });
    setAdding(false);
  };

  const ids = allIds(d);
  const ordered = [...ids].sort((a, b) => ruleOf(d, a).rank - ruleOf(d, b).rank || ids.indexOf(a) - ids.indexOf(b));
  const sharesRank = (id: RowId) => ids.filter((o) => o !== id && ruleOf(d, o).rank === ruleOf(d, id).rank
    && (ruleOf(d, o).enabled || o === 'walk_in'));
  const err = (id: RowId, field: string) => errors[errKey(d, id, field)];

  return (
    <div className="space-y-4">
      <div role="note" className="rounded-xl border border-border bg-danger-soft p-3.5 text-[12.5px] leading-relaxed text-danger-ink">
        <div className="mb-1 font-semibold">Clinical urgency always comes first</div>
        Emergency / Casualty and clinically urgent patients are always seen before anyone these rules order, and a
        consultation in progress is never interrupted by them. These settings only decide the order of routine
        (non-urgent) patients. A patient who needs to be seen sooner for a clinical reason must be marked
        <b> Urgent</b> or <b>Emergency</b> by authorised staff — not given an administrative preference.
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-ink-muted">
        <span>
          Settings for branch <b className="text-ink">{view.branch.name ?? `#${view.branch.id}`}</b>
          {view.branch.code ? ` (${view.branch.code})` : ''} — switch branch in the top bar to edit another site.
        </span>
        <Badge variant={view.settings.is_default ? 'neutral' : 'info'}>
          {view.settings.is_default ? 'Using defaults' : `Version ${view.settings.version}`}
        </Badge>
        {view.settings.updated_by && view.settings.updated_at && (
          <span>Last changed by {view.settings.updated_by}, {new Date(view.settings.updated_at).toLocaleString()}</span>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3.5 py-2.5">
          <div className="text-[12.5px] text-ink-muted">
            {d.rules.custom.length} of {view.limits.max_custom} custom priorities used
          </div>
          <Button size="sm" disabled={!canAdd} onClick={() => setAdding(true)}
            title={canAdd ? undefined : `At most ${view.limits.max_custom} custom priorities`}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add custom priority
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[70px]">Rank</TableHead>
              <TableHead>Patient category</TableHead>
              <TableHead className="w-[90px]">Enabled</TableHead>
              <TableHead className="w-[110px]">Assign</TableHead>
              <TableHead>Eligibility conditions</TableHead>
              <TableHead className="w-[130px]">Waiting-time safeguard</TableHead>
              <TableHead className="w-[150px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <LockedRow position="1st" label="Emergency / Casualty"
              condition="Priority set to Emergency (Queue stage, or the Emergency module's triage). May interrupt a consultation in progress through the existing hold-and-resume rules." />
            <LockedRow position="2nd" label="Clinically urgent patients"
              condition="Priority set to Urgent by staff authorised to change clinical priority." />
            {ordered.map((id) => {
              const r = ruleOf(d, id);
              const shared = sharesRank(id);
              const isOff = !r.enabled;
              const label = labelOf(d, id);
              return (
                <TableRow key={id} className={cn(isOff && 'opacity-60')}>
                  <TableCell>
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-md border border-border bg-background px-1.5 font-mono text-[12px]">{r.rank}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[13px] font-semibold">{label}</span>
                      {isCustom(id) && <Badge variant="info">Custom</Badge>}
                    </div>
                    {shared.length > 0 && !isOff && (
                      <div className="text-[11px] text-ink-muted">Same rank as {shared.map((o) => labelOf(d, o).toLowerCase()).join(', ')} — served together in time order</div>
                    )}
                    {(err(id, 'rank') || err(id, 'label')) && <div className="text-[11px] text-danger">{err(id, 'label') || err(id, 'rank')}</div>}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={r.enabled}
                      disabled={id === 'walk_in'}
                      aria-label={`${label} preference`}
                      onCheckedChange={(v) => setCommon(id, { enabled: v })}
                    />
                  </TableCell>
                  <TableCell>
                    {isCustom(id) ? (
                      <AssignmentBadge mode={customOf(d, id)!.assignment} />
                    ) : id === 'travel' ? (
                      <AssignmentBadge mode={d.rules.travel.assignment} />
                    ) : (
                      <AssignmentBadge mode={view.builtin_assignment[id] ?? 'manual'} fixed />
                    )}
                    {err(id, 'auto_rule') && <div className="text-[11px] text-danger">{err(id, 'auto_rule')}</div>}
                    {err(id, 'local_pincode_prefixes') && <div className="text-[11px] text-danger">{err(id, 'local_pincode_prefixes')}</div>}
                  </TableCell>
                  <TableCell className="text-[12px] text-ink-muted">{conditionText(id, d, view.late_checkin_grace_minutes)}</TableCell>
                  <TableCell className="text-[12px]">
                    {d.safeguard_enabled && r.max_wait_minutes != null ? `After ${r.max_wait_minutes} min` : <span className="text-ink-muted">—</span>}
                    {err(id, 'max_wait_minutes') && <div className="text-[11px] text-danger">{err(id, 'max_wait_minutes')}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" aria-label={`Move ${label} up`}
                        disabled={r.rank <= rMin} onClick={() => setCommon(id, { rank: r.rank - 1 })}>
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" aria-label={`Move ${label} down`}
                        disabled={r.rank >= rMax} onClick={() => setCommon(id, { rank: r.rank + 1 })}>
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className={cn('h-7 w-7', editing === id && 'border-teal text-teal')}
                        aria-label={`Edit ${label}`} aria-expanded={editing === id}
                        onClick={() => setEditing(editing === id ? null : id)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {isCustom(id) && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-danger" aria-label={`Delete ${label}`}
                          onClick={() => removeCustom(id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {editing && (isCustom(editing) ? customOf(d, editing) : true) && (
        <EditPanel
          id={editing}
          draft={d}
          view={view}
          err={(field) => err(editing, field)}
          grace={view.late_checkin_grace_minutes}
          limits={view.limits}
          onBuiltin={setBuiltin}
          onCustom={setCustom}
          onCommon={setCommon}
          onClose={() => setEditing(null)}
        />
      )}

      <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-3.5">
        <Switch checked={d.safeguard_enabled} aria-label="Waiting-time safeguard"
          onCheckedChange={(v) => update({ ...d, safeguard_enabled: v })} />
        <div className="text-[12.5px]">
          <div className="font-semibold">Waiting-time safeguard</div>
          <div className="text-ink-muted">
            A routine patient who has waited longer than their category’s limit (counted from their slot time, or from
            check-in for everyone else) moves ahead of every routine patient who has not, so nobody waits indefinitely
            behind a preference. It never moves anyone ahead of Emergency, Urgent, a consultation in progress or a held
            consultation. Set each category’s limit with ✎ Edit.
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-3.5">
        <div className="text-[12.5px] font-semibold">Calling patients out of queue order</div>
        <div className="mb-2 text-[12.5px] text-ink-muted">
          What happens when staff start a consultation with someone who is not next in line. Emergency patients are
          never held up by this.
        </div>
        <div className="flex flex-col gap-1.5 text-[12.5px]" role="radiogroup" aria-label="Calling patients out of queue order">
          <label className="flex items-start gap-2">
            <input type="radio" name="call-order" className="mt-0.5" checked={d.rules.call_order === 'reason_required'}
              onChange={() => update({ ...d, rules: { ...d.rules, call_order: 'reason_required' } })} />
            <span><b>Ask for a reason</b> (recommended) — staff may see another patient first, but must say why. The
              reason is saved in the audit log.</span>
          </label>
          <label className="flex items-start gap-2">
            <input type="radio" name="call-order" className="mt-0.5" checked={d.rules.call_order === 'off'}
              onChange={() => update({ ...d, rules: { ...d.rules, call_order: 'off' } })} />
            <span><b>Don’t check</b> — staff can start any waiting patient without a reason. The order is a guide only.</span>
          </label>
        </div>
      </div>

      <OrderSummary draft={d} />

      <div className="rounded-xl border border-border bg-card p-3.5">
        <div className="mb-2 text-[12.5px] text-ink-muted">
          <b className="text-ink">What happens when you save:</b> the order of the {view.waiting_now} patient
          {view.waiting_now === 1 ? '' : 's'} waiting at this branch is recalculated straight away. Tokens and check-in
          times do not change, and nobody already with a doctor or on hold is moved. Deleting a custom priority stops it
          applying to patients already ticked with it.
        </div>
        <FormField label="Reason for this change" required error={dirty && !reasonOk && reason ? 'At least 3 characters' : undefined}>
          <Textarea rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Medical superintendent approved priority for senior citizens from 20 Oct" />
        </FormField>
        {stale && (
          <div className="mt-2 rounded-md bg-warn-soft px-3 py-2 text-[12px] text-warn-ink">
            Someone else saved these settings after you opened them. Reload to see their version, then reapply your changes.{' '}
            <Button variant="link" size="sm" onClick={() => q.refetch()}>Reload</Button>
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button disabled={!dirty || !reasonOk || Object.keys(clientErrors).length > 0} loading={save.isPending}
            onClick={() => save.mutate(d)}>
            Save changes
          </Button>
          <Button variant="ghost" disabled={!dirty} onClick={() => { setDraft(clone(loaded!)); setServerErrors({}); setEditing(null); }}>
            Discard changes
          </Button>
          <Button variant="ghost" disabled={isDefault && view.settings.is_default} onClick={() => setConfirmReset(true)}>
            Reset to defaults…
          </Button>
          {dirty && !reasonOk && <span className="self-center text-[11.5px] text-ink-muted">Enter a reason to save.</span>}
        </div>
      </div>

      <HistoryList entries={history.data} loading={history.isLoading} />

      <AddCustomDialog
        open={adding}
        draft={d}
        limits={view.limits}
        onCancel={() => setAdding(false)}
        onAdd={addCustom}
      />

      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset queue prioritization to defaults?</DialogTitle>
            <DialogDescription>
              Referral and travel preferences are switched off, every custom priority is removed, booked patients go
              back to their slot time alongside walk-ins, and every waiting-time limit returns to 120 minutes. The{' '}
              {view.waiting_now} patients waiting now are re-ordered immediately.
            </DialogDescription>
          </DialogHeader>
          <FormField label="Reason" required>
            <Textarea rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
          </FormField>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmReset(false)}>Cancel</Button>
            <Button variant="danger" disabled={!reasonOk} loading={reset.isPending} onClick={() => reset.mutate()}>Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LockedRow({ position, label, condition }: { position: string; label: string; condition: string }) {
  return (
    <TableRow className="bg-muted/40">
      <TableCell><Lock className="h-3.5 w-3.5 text-ink-muted" aria-label="Fixed" /></TableCell>
      <TableCell>
        <div className="text-[13px] font-semibold">{label}</div>
        <div className="text-[11px] text-ink-muted">Always {position} — fixed clinical rule</div>
      </TableCell>
      <TableCell><Badge variant="danger">Always on</Badge></TableCell>
      <TableCell className="text-[11px] text-ink-muted">Clinical decision</TableCell>
      <TableCell className="text-[12px] text-ink-muted">{condition}</TableCell>
      <TableCell className="text-[12px] text-ink-muted">Not needed</TableCell>
      <TableCell className="text-right text-[11px] text-ink-muted">Not editable</TableCell>
    </TableRow>
  );
}

function numOrNull(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

function AssignmentBadge({ mode, fixed = false }: { mode: Assignment; fixed?: boolean }) {
  const text = mode === 'automatic' ? 'Automatic' : 'Manual';
  return (
    <Badge variant={mode === 'automatic' ? 'success' : 'neutral'}
      title={fixed ? 'Fixed for this built-in category' : mode === 'automatic' ? 'Decided by the system from the patient record' : 'Staff tick it at the Queue stage'}>
      {text}{fixed ? ' 🔒' : ''}
    </Badge>
  );
}

/** Manual / Automatic choice plus the automatic rule (age range, gender). Shared by the Add dialog and the Edit panel. */
function AssignmentFields({ assignment, rule, limits, error, onChange }: {
  assignment: Assignment;
  rule: AutoRule | null;
  limits: QueuePriorityAdminView['limits'];
  error?: string;
  onChange: (patch: { assignment?: Assignment; auto_rule?: AutoRule | null }) => void;
}) {
  const r: AutoRule = rule ?? { min_age: null, max_age: null, gender: null };
  const setRule = (patch: Partial<AutoRule>) => onChange({ auto_rule: { ...r, ...patch } });
  return (
    <div className="grid gap-3 sm:col-span-3 sm:grid-cols-3">
      <FormField label="How patients are assigned">
        <NativeSelect value={assignment}
          onChange={(e) => {
            const next = e.target.value as Assignment;
            onChange(next === 'automatic' && !rule ? { assignment: next, auto_rule: r } : { assignment: next });
          }}>
          <option value="manual">Manual — staff tick it at the Queue stage</option>
          <option value="automatic">Automatic — system decides from patient record</option>
        </NativeSelect>
      </FormField>
      {assignment === 'automatic' && (
        <>
          <FormField label="Age from / to (years, inclusive)" error={error}>
            <div className="flex items-center gap-2">
              <Input type="number" min={limits.age[0]} max={limits.age[1]} placeholder="Any" aria-label="Minimum age"
                value={r.min_age ?? ''} onChange={(e) => setRule({ min_age: numOrNull(e.target.value) })} />
              <span className="text-ink-muted">–</span>
              <Input type="number" min={limits.age[0]} max={limits.age[1]} placeholder="Any" aria-label="Maximum age"
                value={r.max_age ?? ''} onChange={(e) => setRule({ max_age: numOrNull(e.target.value) })} />
            </div>
          </FormField>
          <FormField label="Gender">
            <NativeSelect value={r.gender ?? ''}
              onChange={(e) => setRule({ gender: (e.target.value || null) as RuleGender | null })}>
              <option value="">Any</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="other">Other</option>
            </NativeSelect>
          </FormField>
          <div className="text-[11.5px] text-ink-muted sm:col-span-3">
            Applies to {ruleText(r)}. Age comes from the date of birth, or the age recorded at registration; a patient
            with no age recorded is not matched by an age condition. Staff cannot tick an automatic priority.
          </div>
        </>
      )}
    </div>
  );
}

/** "+ Add custom priority": name, optional eligibility note, rank, waiting-time limit. Added to the draft; Save stores it. */
function AddCustomDialog({ open, draft, limits, onCancel, onAdd }: {
  open: boolean;
  draft: Draft;
  limits: QueuePriorityAdminView['limits'];
  onCancel: () => void;
  onAdd: (c: Omit<CustomCategoryRule, 'key'>) => void;
}) {
  const defaultRank = Math.max(limits.rank[0], draft.rules.walk_in.rank - 1);
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [rank, setRank] = useState(defaultRank);
  const [wait, setWait] = useState<number | null>(120);
  const [assignment, setAssignment] = useState<Assignment>('manual');
  const [rule, setRule] = useState<AutoRule | null>(null);
  useEffect(() => {
    if (open) { setLabel(''); setDescription(''); setRank(defaultRank); setWait(120); setAssignment('manual'); setRule(null); }
  }, [open, defaultRank]);

  const name = label.trim();
  const [lMin, lMax] = limits.label_length;
  const [wMin, wMax] = limits.max_wait_minutes;
  const nameError = name.length > 0 && (name.length < lMin || name.length > lMax) ? `${lMin}–${lMax} characters`
    : draft.rules.custom.some((c) => c.label.trim().toLowerCase() === name.toLowerCase()) ? 'A custom priority with this name already exists'
    : undefined;
  const rankError = !Number.isInteger(rank) || rank < limits.rank[0] || rank > limits.rank[1] ? `${limits.rank[0]}–${limits.rank[1]}` : undefined;
  const waitError = wait !== null && (!Number.isInteger(wait) || wait < wMin || wait > wMax) ? `Empty or ${wMin}–${wMax}` : undefined;
  const ruleError = assignment === 'automatic' ? autoRuleError(rule, limits) : undefined;
  const ok = name.length >= lMin && !nameError && !rankError && !waitError && !ruleError;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add custom priority</DialogTitle>
          <DialogDescription>
            Choose Manual (staff tick it for a patient at the Queue stage) or Automatic (the system picks patients by age
            and/or gender). Those patients are ordered by the rank you set here — never ahead of Emergency or Urgent
            patients. It applies once you save the settings.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <FormField label="Name" required error={nameError}>
            <Input value={label} maxLength={lMax} autoFocus onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Senior citizens, Pregnant women, Persons with disabilities" />
          </FormField>
          <FormField label="Who qualifies (shown to staff)">
            <Textarea rows={2} maxLength={limits.description_length} value={description}
              onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Age 60 or over — check ID if unsure" />
          </FormField>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label={`Rank (${limits.rank[0]} = served first)`} error={rankError}>
              <Input type="number" min={limits.rank[0]} max={limits.rank[1]} value={rank}
                onChange={(e) => setRank(Number(e.target.value))} />
            </FormField>
            <FormField label="Waiting-time limit (minutes)" error={waitError}>
              <Input type="number" min={wMin} max={wMax} value={wait ?? ''} placeholder="No limit"
                onChange={(e) => setWait(numOrNull(e.target.value))} />
            </FormField>
          </div>
          <AssignmentFields assignment={assignment} rule={rule} limits={limits} error={ruleError}
            onChange={(p) => { if (p.assignment) setAssignment(p.assignment); if (p.auto_rule !== undefined) setRule(p.auto_rule); }} />
          <div className="text-[11.5px] text-ink-muted">
            Walk-ins are currently rank {draft.rules.walk_in.rank}. A lower number than that puts these patients ahead of walk-ins.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button disabled={!ok}
            onClick={() => onAdd({
              label: name, description: description.trim() || null, enabled: true, rank, max_wait_minutes: wait,
              assignment, auto_rule: assignment === 'automatic' ? rule : null,
            })}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditPanel({ id, draft, view, err, grace, limits, onBuiltin, onCustom, onCommon, onClose }: {
  id: RowId;
  draft: Draft;
  view: QueuePriorityAdminView;
  err: (field: string) => string | undefined;
  grace: number;
  limits: QueuePriorityAdminView['limits'];
  onBuiltin: <C extends RoutineCategory>(c: C, patch: Partial<QueueRules[C]>) => void;
  onCustom: (key: CustomKey, patch: Partial<CustomCategoryRule>) => void;
  onCommon: (id: RowId, patch: Partial<CommonRule>) => void;
  onClose: () => void;
}) {
  const r = ruleOf(draft, id);
  const custom = isCustom(id) ? customOf(draft, id) : undefined;
  return (
    <div className="rounded-xl border border-teal bg-card p-3.5">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[13px] font-semibold">Edit: {labelOf(draft, id)}</div>
        <Button size="sm" variant="ghost" onClick={onClose}>Done</Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {custom && (
          <FormField label="Name" error={err('label')}>
            <Input value={custom.label} maxLength={limits.label_length[1]}
              onChange={(e) => onCustom(custom.key, { label: e.target.value })} />
          </FormField>
        )}
        <FormField label={`Rank (${limits.rank[0]} = served first)`} error={err('rank')}>
          <Input type="number" min={limits.rank[0]} max={limits.rank[1]} value={r.rank}
            onChange={(e) => onCommon(id, { rank: Number(e.target.value) })} />
        </FormField>
        <FormField label="Waiting-time limit (minutes)" error={err('max_wait_minutes')}>
          <Input type="number" min={limits.max_wait_minutes[0]} max={limits.max_wait_minutes[1]}
            value={r.max_wait_minutes ?? ''} placeholder="No limit"
            onChange={(e) => onCommon(id, { max_wait_minutes: numOrNull(e.target.value) })} />
        </FormField>

        {id === 'appointment' && (
          <FormField label="Appointment policy" error={err('policy')}>
            <NativeSelect value={draft.rules.appointment.policy}
              onChange={(e) => onBuiltin('appointment', { policy: e.target.value as QueueRules['appointment']['policy'] })}>
              <option value="slot_time">Honour slot time (up to {grace} min late)</option>
              <option value="check_in_time">Order by check-in time</option>
            </NativeSelect>
          </FormField>
        )}
        {id === 'referral' && (
          <label className="flex items-center gap-2 self-end pb-2 text-[12.5px]">
            <Checkbox checked={draft.rules.referral.require_referral_source}
              onCheckedChange={(v) => onBuiltin('referral', { require_referral_source: v === true })} />
            Only when the referring doctor / facility is recorded
          </label>
        )}
        {id === 'travel' && (
          <TravelFields draft={draft} view={view} limits={limits} err={err} onBuiltin={onBuiltin} />
        )}
        {custom && (
          <AssignmentFields assignment={custom.assignment} rule={custom.auto_rule} limits={limits} error={err('auto_rule')}
            onChange={(p) => onCustom(custom.key, p)} />
        )}
        {custom && (
          <FormField label="Who qualifies (shown to staff)" className="sm:col-span-3" error={err('description')}>
            <Textarea rows={2} maxLength={limits.description_length} value={custom.description ?? ''}
              onChange={(e) => onCustom(custom.key, { description: e.target.value || null })} />
          </FormField>
        )}
      </div>
      <div className="mt-2 text-[11.5px] text-ink-muted">
        {id === 'appointment' && `The late-check-in grace (${grace} min) is set in Facility → Doctor Availability.`}
        {id === 'referral' && 'An urgent referral is not a routine referral — mark that patient Urgent instead.'}
        {id === 'travel' && (draft.rules.travel.assignment === 'automatic'
          ? 'Uses the district, state and PIN code on the patient record. Patients with no address recorded are treated as walk-ins.'
          : 'Staff record the distance the patient travelled at the Queue stage.')}
        {id === 'walk_in' && 'Walk-ins are always ordered by check-in time.'}
        {custom && 'A patient who qualifies for several priorities gets the best rank among them.'}
      </div>
    </div>
  );
}

/** Long-distance travel: Manual (staff enter the km) or Automatic (patient address vs this branch's address). */
function TravelFields({ draft, view, limits, err, onBuiltin }: {
  draft: Draft;
  view: QueuePriorityAdminView;
  limits: QueuePriorityAdminView['limits'];
  err: (field: string) => string | undefined;
  onBuiltin: <C extends RoutineCategory>(c: C, patch: Partial<QueueRules[C]>) => void;
}) {
  const t = draft.rules.travel;
  const [prefixText, setPrefixText] = useState(t.local_pincode_prefixes.join(', '));
  useEffect(() => { setPrefixText(t.local_pincode_prefixes.join(', ')); }, [t.local_pincode_prefixes]);
  const branchValue = t.auto_basis === 'district' ? view.branch.address_district
    : t.auto_basis === 'state' ? view.branch.address_state : null;
  const basisLabel = t.auto_basis === 'district' ? 'district' : 'state';

  return (
    <>
      <FormField label="How patients are assigned">
        <NativeSelect value={t.assignment} onChange={(e) => onBuiltin('travel', { assignment: e.target.value as Assignment })}>
          <option value="manual">Manual — staff enter the km at the Queue stage</option>
          <option value="automatic">Automatic — from the patient’s address</option>
        </NativeSelect>
      </FormField>
      {t.assignment === 'manual' ? (
        <FormField label="Minimum travel distance (km)" error={err('min_distance_km')}>
          <Input type="number" min={limits.min_distance_km[0]} max={limits.min_distance_km[1]}
            value={t.min_distance_km}
            onChange={(e) => onBuiltin('travel', { min_distance_km: Number(e.target.value) })} />
        </FormField>
      ) : (
        <>
          <FormField label="Counts as long distance when the patient lives">
            <NativeSelect value={t.auto_basis} onChange={(e) => onBuiltin('travel', { auto_basis: e.target.value as TravelBasis })}>
              <option value="district">In a different district</option>
              <option value="state">In a different state</option>
              <option value="pincode">Outside the local PIN codes</option>
            </NativeSelect>
          </FormField>
          {t.auto_basis === 'pincode' ? (
            <FormField label="Local PIN codes (beginnings, comma-separated)" className="sm:col-span-3" error={err('local_pincode_prefixes')}>
              <Input value={prefixText} placeholder="e.g. 600, 601, 603"
                onChange={(e) => setPrefixText(e.target.value)}
                onBlur={() => onBuiltin('travel', {
                  local_pincode_prefixes: [...new Set(prefixText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean))],
                })} />
            </FormField>
          ) : (
            <div className="text-[12px] text-ink-muted sm:col-span-3">
              {branchValue ? (
                <>Compared with this branch’s {basisLabel}: <b className="text-ink">{branchValue}</b>.</>
              ) : (
                <span className="text-danger">
                  This branch has no {basisLabel} in its address, so nobody will match. Add it in Facility → Branches.
                </span>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

/** The effective precedence, in words, for the draft being edited. */
function OrderSummary({ draft }: { draft: Draft }) {
  const tiers = new Map<number, RowId[]>();
  for (const id of allIds(draft)) {
    const r = ruleOf(draft, id);
    if (!r.enabled && id !== 'walk_in') continue;
    tiers.set(r.rank, [...(tiers.get(r.rank) ?? []), id]);
  }
  const steps = [
    'The patient already with the doctor (never moved)',
    'Emergency / Casualty',
    'Clinically urgent',
    'A consultation on hold, before anyone new at the same priority',
    ...(draft.safeguard_enabled ? ['Routine patients past their waiting-time limit, longest overdue first'] : []),
    ...[...tiers.entries()].sort(([a], [b]) => a - b)
      .map(([, cats]) => cats.map((c) => labelOf(draft, c)).join(' + ') + (cats.length > 1 ? ', together in time order' : '')),
  ];
  return (
    <div className="rounded-xl border border-border bg-card p-3.5">
      <div className="mb-1.5 text-[12.5px] font-semibold">How the queue will be ordered</div>
      <ol className="list-decimal space-y-0.5 pl-5 text-[12px] text-ink-muted">
        {steps.map((s) => <li key={s}>{s}</li>)}
        <li>Ties: earlier check-in first</li>
      </ol>
    </div>
  );
}

function fmtValue(v: unknown): string {
  if (v === null || v === undefined) return 'none';
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  if (typeof v === 'object' && 'label' in (v as Record<string, unknown>)) return `“${String((v as { label: unknown }).label)}”`;
  if (typeof v === 'object' && 'min_age' in (v as Record<string, unknown>)) return ruleText(v as AutoRule);
  return String(v);
}

function fmtField(field: string): string {
  return field.replace(/^rules\./, '').replace(/^custom\.c_[a-z0-9_]+$/, 'custom priority');
}

function HistoryList({ entries, loading }: { entries?: Awaited<ReturnType<typeof qpApi.getQueuePriorityHistory>>; loading: boolean }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3.5">
      <div className="mb-2 text-[12.5px] font-semibold">Recent changes at this branch</div>
      {loading && <div className="text-[12px] text-ink-muted">Loading…</div>}
      {!loading && (!entries || entries.length === 0) && <div className="text-[12px] text-ink-muted">No changes yet — the defaults are in use.</div>}
      <ul className="space-y-2">
        {(entries ?? []).map((h) => (
          <li key={h.id} className="border-l-2 border-border pl-3 text-[12px]">
            <div>
              <b>{h.event === 'queue_priority_settings_reset' ? 'Reset to defaults' : 'Updated'}</b>
              {' '}by {h.changed_by}{h.actor_role ? ` (${h.actor_role})` : ''} · {new Date(h.changed_at).toLocaleString()}
              {h.version != null && <span className="text-ink-muted"> · v{h.version}</span>}
            </div>
            {h.reason && <div className="text-ink-muted">Reason: {h.reason}</div>}
            {h.changes && h.changes.length > 0 && (
              <div className="text-ink-muted">
                {h.changes.map((c) => `${fmtField(c.field)}: ${fmtValue(c.from)} → ${fmtValue(c.to)}`).join('; ')}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
